import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

const PICKER_PAGE_SIZE = 10;
const PANEL_WIDTH = 280;
const PANEL_Z_INDEX = 10001;

const BillingPicker = ({ label, value, options = [], onChange, menuAlign = 'left' }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [panelStyle, setPanelStyle] = useState(null);
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const toggleRef = useRef(null);

  const normalizedOptions = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        value: String(option.value),
        label: option.label || String(option.value),
      })),
    [options]
  );

  const selected = useMemo(
    () =>
      normalizedOptions.find((option) => option.value === String(value)) ||
      normalizedOptions[0] || { value: 'all', label: 'All' },
    [normalizedOptions, value]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) {
      return normalizedOptions;
    }
    const q = search.toLowerCase();
    return normalizedOptions.filter(
      (option) =>
        String(option.label).toLowerCase().includes(q) ||
        String(option.value).toLowerCase().includes(q)
    );
  }, [normalizedOptions, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PICKER_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PICKER_PAGE_SIZE;
  const visibleOptions = filtered.slice(startIndex, startIndex + PICKER_PAGE_SIZE);

  const updatePanelPosition = useCallback(() => {
    if (!toggleRef.current) {
      return;
    }

    const rect = toggleRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    let left =
      menuAlign === 'right' ? rect.right - PANEL_WIDTH : rect.left;
    left = Math.max(8, Math.min(left, viewportWidth - PANEL_WIDTH - 8));

    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left,
      width: PANEL_WIDTH,
      zIndex: PANEL_Z_INDEX,
    });
  }, [menuAlign]);

  useEffect(() => {
    setPage(1);
  }, [search, normalizedOptions]);

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }

    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);

    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open, updatePanelPosition]);

  useEffect(() => {
    function handleClick(event) {
      const target = event.target;
      if (
        (rootRef && rootRef.current && rootRef.current.contains(target)) ||
        (panelRef && panelRef.current && panelRef.current.contains(target))
      ) {
        return;
      }
      setOpen(false);
    }

    if (open) {
      document.addEventListener('mousedown', handleClick);
    }

    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleSelect = useCallback(
    (nextValue) => {
      setOpen(false);
      if (onChange) {
        onChange(nextValue);
      }
    },
    [onChange]
  );

  const handlePageChange = (direction) => {
    setPage((prev) => {
      if (direction === 'prev') {
        return Math.max(1, prev - 1);
      }
      if (direction === 'next') {
        return Math.min(totalPages, prev + 1);
      }
      return prev;
    });
  };

  const panel =
    open &&
    panelStyle &&
    createPortal(
      <div
        ref={panelRef}
        style={{
          ...panelStyle,
          background: '#fff',
          border: '1px solid #d1d5db',
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)',
          padding: 10,
        }}
      >
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${String(label).toLowerCase()}...`}
          style={{
            width: '100%',
            border: '1px solid #d1d5db',
            borderRadius: 4,
            padding: '6px 8px',
            fontSize: '0.85rem',
            marginBottom: 8,
          }}
        />
        <div
          style={{
            maxHeight: 220,
            overflowY: 'auto',
            border: '1px solid #e5e7eb',
            borderRadius: 4,
          }}
        >
          {visibleOptions.length ? (
            visibleOptions.map((option) => (
              <div
                key={option.value}
                onClick={() => handleSelect(option.value)}
                role="button"
                style={{
                  padding: '8px 10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '0.85rem',
                  background:
                    option.value === String(selected.value)
                      ? '#e0ebff'
                      : 'transparent',
                  color:
                    option.value === String(selected.value)
                      ? '#1d4ed8'
                      : '#111827',
                  fontWeight:
                    option.value === String(selected.value) ? 600 : 400,
                }}
              >
                <span>{option.label}</span>
                {option.value === String(selected.value) && (
                  <span style={{ fontSize: '0.8rem', marginLeft: 8 }}>✔</span>
                )}
              </div>
            ))
          ) : (
            <div
              style={{
                textAlign: 'center',
                padding: '12px 0',
                fontSize: 12,
                color: '#6b7280',
              }}
            >
              No results found.
            </div>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 8,
            fontSize: 12,
            color: '#6b7280',
          }}
        >
          <div>
            Page {totalPages ? currentPage : 0} of {totalPages}
          </div>
          <div>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handlePageChange('prev')}
              disabled={currentPage === 1}
              style={{ marginRight: 4 }}
            >
              Prev
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handlePageChange('next')}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <div
      ref={rootRef}
      style={{ position: 'relative', minWidth: 200, maxWidth: 260 }}
    >
      <button
        ref={toggleRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: '100%',
          border: '1px solid #d1d5db',
          background: '#fff',
          borderRadius: 6,
          padding: '6px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.85rem',
          color: '#374151',
          cursor: 'pointer',
        }}
      >
        <span style={{ textAlign: 'left' }}>
          <div
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              color: '#6b7280',
              marginBottom: 2,
            }}
          >
            {label}
          </div>
          <div style={{ fontWeight: 600, color: '#111827' }}>
            {selected && selected.label ? selected.label : 'Select'}
          </div>
        </span>
        <span
          className={`icon-arrow-${open ? 'up' : 'down'}2`}
          style={{ marginLeft: 6 }}
        />
      </button>
      {panel}
    </div>
  );
};

export default BillingPicker;



// WEBPACK FOOTER //
// ./src/components/billing/BillingPicker.js